import { LogManager } from "@mcleod/core";
import { BlurEvent, ChangeEvent, Layout, Snackbar, Switch, Textbox, TooltipContent } from "@mcleod/components";
import { CommonDialogs, YesNoDialogProps } from "@mcleod/common";
import { AutogenLayoutMappingVendors } from "./autogen/AutogenLayoutMappingVendors";

const log = LogManager.getLogger("lme/powerbroker/MappingVendors");
export class MappingVendors extends AutogenLayoutMappingVendors {

    onLoad() {
        this.setGoogleMappingVersionTooltip();
    }

    async postDataSources(vendorId: string): Promise<boolean> {
        try {

            if (this.mainDataSource?.hasChanged()) {
                this.mainDataSource?.activeRow?.set("vendor", vendorId);
                await this.mainDataSource.post();
            }

            if (this.sourceDispatchControl?.hasChanged()) {
                await this.sourceDispatchControl.post();
            }

            if (this.sourceDistanceSource?.hasChanged()) {
                await this.sourceDistanceSource.post();
            }

            if (this.layoutMappingControl.mainDataSource?.hasChanged()) {
                await this.layoutMappingControl.mainDataSource.post();
            }

            return true;
        } catch (error) {
            log.error(error);
            return false;
        }
    }

    textboxEntryDelayOnBlur(event: BlurEvent) {
        const searchDelay = (event.target as Textbox).text;
        if (searchDelay !== "" && searchDelay !== null && searchDelay !== undefined) {
            const isNumeric = /^\d+$/.test(searchDelay);
            if (!isNumeric) {
                Snackbar.showWarningSnackbar("Only positive numeric values are allowed.");
                if (event.target.id === this.textboxGooglePlacesEntryDelay.id) {
                    this.sourceDispatchControl?.activeRow.set("google_places_entry_delay", 750);
                }
            }
        }
    }

    async switchEnableGooglePlacesOnChange(event: ChangeEvent) {
        const isEnabledGooglePlaces = this.switchEnableGooglePlaces.checked;
        this.setVisibility(this.switchEnableAddLocationGooglePlaces, isEnabledGooglePlaces);
        this.setVisibility(this.switchEnableAddCustomerGooglePlaces, isEnabledGooglePlaces);
        this.setVisibility(this.switchEnableAddStopGooglePlaces, isEnabledGooglePlaces);
        this.setVisibility(this.switchCreateLocationFromStopGooglePlaces, isEnabledGooglePlaces);
        this.setVisibility(this.switchAllowDuplicateLocationCreationGooglePlaces, isEnabledGooglePlaces);
        this.setVisibility(this.textboxGooglePlacesEntryDelay, isEnabledGooglePlaces);
        if (this.sourceDispatchControl?.hasChanged()) {
            if (isEnabledGooglePlaces) {
                await this.validateGooglePlaces();
            }
        }
    }

    switchTripManagementActiveOnChange(event: ChangeEvent) {
        const isSwitchToggleEnabled = this.switchTripManagementActive.checked;
        this.setVisibility(this.textboxCompanyId, isSwitchToggleEnabled);
        this.setVisibility(this.textboxDistanceProfile, isSwitchToggleEnabled);
        this.setVisibility(this.textboxApiKeyTrip, isSwitchToggleEnabled);
        this.setVisibility(this.textboxPosUpdateMinutes, isSwitchToggleEnabled);
        this.setVisibility(this.textboxWeatherUrl, isSwitchToggleEnabled);
        this.setVisibility(this.textboxUrl, isSwitchToggleEnabled);
        this.setVisibility(this.textboxOutOfCorridorUrl, isSwitchToggleEnabled);
    }

    setVisibility = (component: Switch | Textbox, switchComponentCheck: boolean) => {
        component.visible = switchComponentCheck;
        component.visibleDuringAdd = switchComponentCheck;
        component.visibleDuringUpdate = switchComponentCheck;
    };

    private async validateGooglePlaces() {
        const PC_MILER_WEB = "T";
        const distanceSearchResponse = await this.sourceDistanceSource.search({"id": PC_MILER_WEB});
        if (distanceSearchResponse.modelRows.length > 0) {
            const data = distanceSearchResponse.modelRows[0]?.data;
            const enableTrimblePlaces: string = data?.enable_trimble_places;
            if ("Y" === enableTrimblePlaces) {
                const content = this.createContent("Trimble", "Google");
                const title = "Confirm Changes";
                const dialogProps: Partial<YesNoDialogProps> = { noButtonCaption: "No", yesButtonCaption: "Yes" };
                const userChoice = await CommonDialogs.showYesNo(content, title, dialogProps);
                if (userChoice === true) {
                    this.setDistanceSourceActiveRow();
                    this.sourceDistanceSource.post();
                } else {
                    this.sourceDispatchControl.activeRow.set("enable_google_places", "N");
                }
            }
        }
    }

    setDistanceSourceActiveRow() {
        this.sourceDistanceSource.activeRow.set({enable_trimble_places: "N", enable_add_location_trimble: "N",enable_add_customer_trimble: "N",
            enable_stop_lookup: "N", create_location_from_stop: "N",webservice_add_duplicate: "N", trimble_places_entry_delay: 750});
    }

    setDispatchSourceActiveRow() {
        this.sourceDispatchControl.activeRow.set({enable_google_places: "N", enable_location_add_google: "N",enable_customer_add_google: "N",
            enable_stop_add_google: "N", create_location_from_stop: "N", enable_duplicate_add_google: "N", google_places_entry_delay: 750});
    }

    createContent(currentService: string, newService: string) {
        return `${currentService} Places is currently active. Only one places service can be active. Are you sure you want to set ${newService} Places as the active service?`;
    }

    private setGoogleMappingVersionTooltip() {
        this.textboxVersion.tooltipDynamicContent = async () => {
            return await Layout.getLoadedLayout("lme/powerbroker/GoogleMappingVersionQuickInfo");
        };
    }
}
